import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { RouteComponentProps } from "react-router";
import { Audit } from "../../constants/interfaces";
import routes from "../../constants/routes";
import RxTable, {
  Table,
  IColumn,
  TableDetailCard,
  SearchBar,
  CustomWidget,
  FilterBar
} from "../../components/RxTable";
import {
  PageHeader,
  PageRoot,
  TypeaheadSelect,
  Select,
  Icon
} from "../../components";
import { auditActions } from "../../reducers/audit";
import formatDate from "../../utils/formatDate";
import downloadFile from "../../utils/downloadFile";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    padding: "2em"
  },
  toolBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
  },
  select: {
    marginBottom: theme.spacing(1),
    width: "50%"
  },
  fullWidthSelect: {
    marginBottom: theme.spacing(1),
    width: "100%"
  }
}));

type Props = RouteComponentProps & {
  classes?: any;
  audits: Audit[];
  auditStatusMapper: {
    [Key: string]: string;
  };
  error?: Error;
  loading: boolean;
  getAudits: () => Promise<void>;
};

const AuditHistory: React.FC<Props> = props => {
  const classes = useStyles();

  const columns = useMemo(() => {
    const columns: IColumn[] = [
      {
        id: "legalEntityName",
        label: "Legal Entity",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="legalEntityName"
            key="legalEntityName"
            placeholder="Legal entity"
            label="Legal Entity"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "premisesName",
        label: "Premises",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="premisesName"
            key="premisesName"
            placeholder="Premises"
            label="Premises"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },

      {
        id: "auditType",
        label: "Audit Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditType"
            key="auditType"
            label="Audit Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },

      {
        id: "auditSubType",
        label: "Audit Sub Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditSubType"
            key="auditSubType"
            label="Audit Sub Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditToolType",
        label: "Audit Tool Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditToolType"
            key="auditToolType"
            label="Audit Tool Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditDate",
        label: "Audit Date",
        type: "date",
        searchable: true,
        parse: formatDate.condensed,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditDate"
            key="auditDate"
            label="Audit Date"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "status",
        label: "Status",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="status"
            key="status"
            label="Status"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        parse: status => props.auditStatusMapper[status] || status
      },
      {
        id: "auditReport",
        type: "empty",
        label: "Audit Report",
        meta: {
          hideInDetail: true
        },
        renderCell: (cell, row) => (
          <Icon
            color="primary"
            name="get_app"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              const token = window.localStorage.getItem("token");

              if (!token) throw new Error("Invalid Token");
              if (!row.caseId) throw new Error("Invalid caseId");

              const url = `/mcols/api/audits/downloadAuditReport?caseId=${row.caseId}&token=${token}`;
              downloadFile({ url });
            }}
          />
        )
      },
      {
        id: "camsLink",
        label: "Corrective Action Status",
        type: "empty",
        meta: {
          hideInDetail: true
        },
        renderCell: (data, row) => (
          <Icon
            color="primary"
            name="reply"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              props.history.push(`${routes.CAM_LIST.url}${row.caseId}`);
            }}
          />
        )
      }
    ];
    return columns;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    props.getAudits();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
        label={routes.AUDIT_HISTORY.label}
      />
      <RxTable>
        <div className={classes.toolBar}>
          <SearchBar />
          <CustomWidget
            compare={(next, prev) =>
              next.rawData === prev.rawData && next.filters === prev.filters
            }
          >
            {({ dispatch, filters, columns, rawData }) => (
              <FilterBar
                filters={filters}
                dispatch={dispatch}
                data={rawData}
                columns={columns}
              />
            )}
          </CustomWidget>
        </div>

        <Table
          columns={columns}
          loading={props.loading}
          sorting={{ sortDirection: "desc", id: "auditDate" }}
          data={props.audits}
          rowsPerPage={50}
          getRowId={row => row.caseId}
        />

        <TableDetailCard title={row => row.premisesName} columns={columns} />
      </RxTable>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getAudits: auditActions.getAudits
};

const mapStateToProps = ({ audit }: any) => {
  return {
    audits: audit.audits,
    auditStatusMapper: audit.auditStatusMapper,
    error: audit.error,
    loading: audit.loading
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AuditHistory);
